/**
 * CountryPostArrival — canonical "what happens after you land" body,
 * shared between the public /countries/{country} dossier and the private,
 * personalised /dashboard.
 *
 * Same data source of truth:
 *   - GET /api/countries/{country}/vendor-links   (scope-aware referral + Nikshala rail)
 *   - GET /api/post-arrival/{country}             (six-category rails + registration)
 *
 * Personalisation:
 *   - `hostCity` (optional) — when provided, the Legal & Identity rail highlights
 *     the specific registration office for that city (Germany style) or, for
 *     countries with a national portal (France), simply shows the national link.
 *   - `dashboardMode` — flag to render the "For your case in {hostCity}" copy
 *     instead of the generic dossier headline.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ExternalLink, MapPin, Info, ChevronRight, Landmark, AlertTriangle, GitFork, ArrowUpRight } from "lucide-react";
import LocalLifeTabs from "./LocalLifeTabs";

export default function CountryPostArrival({ country, hostCity, dashboardMode = false, compactMerged = false }) {
  const [pa, setPa] = useState(null);
  const [paLoaded, setPaLoaded] = useState(false);
  const [vendor, setVendor] = useState(null);

  useEffect(() => {
    if (!country) return;
    setPaLoaded(false);
    api.get(`/post-arrival/${encodeURIComponent(country)}`)
      .then(r => { setPa(r.data); setPaLoaded(true); })
      .catch(() => { setPa(null); setPaLoaded(true); });
    api.get(`/countries/${encodeURIComponent(country)}/vendor-links`).then(r => setVendor(r.data)).catch(() => setVendor(null));
  }, [country]);

  const matchedOffice = hostCity && pa?.city_offices
    ? pa.city_offices.find(c => c.city.toLowerCase() === hostCity.toLowerCase())
    : null;

  return (
    <>
      {/* ================= NIKSHALA PARTNER SERVICES (vendor_links) ================= */}
      {vendor?.count > 0 && (
        <div className="mt-10 moe-card p-6 md:p-8" data-testid="section-vendor-links">
          <div className="text-xs uppercase tracking-widest text-elec font-bold">Nikshala partner services</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
            {dashboardMode
              ? `Services for your ${vendor.country} move`
              : `Services in scope for ${vendor.country}`}
          </h2>
          <p className="mt-2 text-sm text-muted max-w-2xl">
            Vetted partners for the practical parts of your move. Referral partners help fund Ministry of Exchange. Nikshala products are shipped in-house.
          </p>

          <div className="mt-6 space-y-6" data-testid="vendor-categories">
            {vendor.categories.map((cat) => (
              <div key={cat.key} data-testid={`vendor-cat-${cat.key}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{cat.label}</div>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {cat.items.map((v) => (
                    <a
                      key={v.provider_name}
                      href={v.url}
                      target="_blank"
                      rel={v.link_type === "referral" ? "sponsored noopener" : "noopener"}
                      title={v.display_disclosure}
                      data-testid={`vendor-${cat.key}-${v.provider_name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="flex items-center justify-between gap-3 border border-ink/15 rounded-lg px-3.5 py-2.5 hover:bg-tint transition-colors group min-w-0"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="font-bold text-ink text-sm truncate">{v.provider_name}</div>
                          <span
                            className={
                              v.link_type === "own_product"
                                ? "text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-ink text-white shrink-0"
                                : v.link_type === "free_alternative"
                                  ? "text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border border-ink/30 text-ink shrink-0"
                                  : "text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-elec/10 text-elec shrink-0"
                            }
                          >
                            {v.link_type === "own_product"
                              ? "Nikshala"
                              : v.link_type === "free_alternative"
                                ? "Free"
                                : "Partner"}
                          </span>
                        </div>
                        {v.note && <div className="text-[11px] text-muted truncate mt-0.5">{v.note}</div>}
                      </div>
                      <ExternalLink size={12} className="text-ink/50 group-hover:text-elec shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-start gap-2 text-[12px] text-muted p-3 rounded-lg bg-tint" data-testid="vendor-disclosure">
            <Info size={13} className="mt-0.5 shrink-0" />
            <div>
              <div><span className="font-semibold text-ink">Partner links:</span> {vendor.disclosure_referral}</div>
              <div className="mt-1"><span className="font-semibold text-ink">Nikshala products:</span> {vendor.disclosure_own_product}.</div>
            </div>
          </div>
        </div>
      )}

      {/* ================= SIX-CATEGORY POST-ARRIVAL RAILS ================= */}
      {pa && (
        <>
          {/* ===== VISA FORK (UK-style two-route decision). First, above Legal & Identity ===== */}
          {pa.visa_fork && (
            <div
              className="mt-10 p-6 md:p-8 rounded-2xl border-2 border-elec bg-elec/5"
              data-testid="section-visa-fork"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-widest text-elec font-bold">First decision</div>
                <span
                  data-testid="visa-fork-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-elec text-white"
                >
                  <GitFork size={10} /> Two possible routes
                </span>
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">{pa.visa_fork.title}</h2>
              {pa.visa_fork.question && (
                <p className="mt-3 text-sm text-ink leading-relaxed max-w-3xl">{pa.visa_fork.question}</p>
              )}
              {pa.visa_fork.decision_criterion && (
                <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-soft font-bold">
                  Decision criterion · {pa.visa_fork.decision_criterion}
                </div>
              )}

              <div className="mt-4 grid md:grid-cols-2 gap-3" data-testid="visa-fork-routes">
                {(pa.visa_fork.routes || []).map((r) => (
                  <div
                    key={r.letter}
                    className="rounded-xl border border-ink/20 bg-white p-5 flex flex-col"
                    data-testid={`visa-route-${r.letter.toLowerCase()}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 rounded-full bg-ink text-white text-sm font-bold flex items-center justify-center shrink-0">{r.letter}</span>
                      <div className="min-w-0">
                        <div className="font-bold text-ink leading-tight">{r.name}</div>
                        {r.when && <div className="text-[11px] text-muted mt-1 leading-snug">{r.when}</div>}
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                      <div>
                        <div className="uppercase tracking-widest text-[9px] text-soft font-bold">Fee</div>
                        <div className="font-bold text-ink">{r.fee}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-widest text-[9px] text-soft font-bold">Health surcharge</div>
                        <div className="font-bold text-ink">{r.ihs}</div>
                      </div>
                      {r.duration_limit && (
                        <div className="col-span-2">
                          <div className="uppercase tracking-widest text-[9px] text-soft font-bold">Max duration</div>
                          <div className="font-bold text-ink">{r.duration_limit}</div>
                        </div>
                      )}
                    </div>
                    {r.constraints?.length > 0 && (
                      <ul className="mt-4 space-y-1 text-[12px] text-ink">
                        {r.constraints.map((c, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-ink shrink-0" />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {r.when_to_use && (
                      <div className="mt-4 text-[11px] text-muted italic leading-snug border-t border-ink/10 pt-3">{r.when_to_use}</div>
                    )}
                  </div>
                ))}
              </div>

              {pa.visa_fork.prompt && (
                <div className="mt-5 p-3 rounded-lg bg-white border border-ink/15" data-testid="visa-fork-prompt">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold mb-1">Message you can send now</div>
                  <div className="text-[13px] text-ink leading-snug">{pa.visa_fork.prompt}</div>
                </div>
              )}
            </div>
          )}

          {/* LEGAL & IDENTITY. City-based (Germany) OR national portal (France/UK).
              In `compactMerged` (dashboard) mode this section, along with Home,
              Money, and Mobility below, is suppressed here and rendered by
              <LocalLifeTabs> instead. See Dashboard.jsx. */}
          {!compactMerged && (<>
          <div
            className={
              "mt-6 p-6 md:p-8 rounded-2xl " +
              (pa.priority_action?.severity === "blocking"
                ? "border-2 border-ink bg-ink text-white"
                : pa.priority_action?.severity === "urgent"
                  ? "border-2 border-[color:var(--moe-pink)] bg-[color:var(--moe-pink)]/10"
                  : "moe-card")
            }
            data-testid="section-legal-identity"
            data-severity={pa.priority_action?.severity || "normal"}
          >
            <div className="flex items-center gap-2">
              <div className={"text-xs uppercase tracking-widest font-bold " + (pa.priority_action?.severity === "blocking" ? "text-[color:var(--moe-elec)]" : "text-elec")}>Legal & identity</div>
              {pa.priority_action?.severity === "urgent" && (
                <span
                  data-testid="urgency-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-ink text-white"
                >
                  Most urgent · {pa.registration_deadline_days ? `${pa.registration_deadline_days} days` : "act now"}
                </span>
              )}
              {pa.priority_action?.severity === "blocking" && (
                <span
                  data-testid="blocking-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[color:var(--moe-elec)] text-white"
                >
                  Blocking · gates everything downstream
                </span>
              )}
            </div>
            <h2 className={"mt-2 text-2xl md:text-3xl font-bold tracking-tight " + (pa.priority_action?.severity === "blocking" ? "text-white" : "")}>
              {pa.priority_action.title} · {pa.priority_action.deadline}
            </h2>
            {pa.priority_action.why && (
              <p className={"mt-2 text-sm max-w-2xl " + (pa.priority_action?.severity === "blocking" ? "text-white/80" : "text-muted")}>{pa.priority_action.why}</p>
            )}

            {/* Denmark-style "these things are blocked until you complete this task" list */}
            {pa.priority_action?.gates?.length > 0 && (
              <div className="mt-4" data-testid="priority-gates">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[color:var(--moe-yolk)]">Blocked until this is done</div>
                <ul className="mt-2 space-y-1.5">
                  {pa.priority_action.gates.map((g, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--moe-yolk)] shrink-0" />
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Ordered steps. Used by Italy's postal-kit sequence. */}
            {pa.priority_action?.steps?.length > 0 && (
              <ol className="mt-5 space-y-2 max-w-2xl" data-testid="priority-steps">
                {pa.priority_action.steps.map((s, i) => (
                  <li key={i} className={"flex items-start gap-3 text-sm " + (pa.priority_action?.severity === "blocking" ? "text-white" : "text-ink")}>
                    <span className={"w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5 " + (pa.priority_action?.severity === "blocking" ? "bg-[color:var(--moe-yolk)] text-ink" : "bg-ink text-white")}>{i + 1}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            )}

            {/* Personalised city callout (dashboard) */}
            {dashboardMode && hostCity && matchedOffice && (
              <div className="mt-5 border-2 border-elec rounded-xl p-4 bg-elec/5" data-testid="dash-matched-office">
                <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Your host city · {matchedOffice.city}</div>
                <a href={matchedOffice.url} target="_blank" rel="noopener" className="mt-2 inline-flex items-center gap-2 font-bold text-ink hover:text-elec">
                  Open {matchedOffice.city} registration portal
                  <ExternalLink size={14} />
                </a>
                {matchedOffice.note && <div className="mt-1 text-xs text-muted">{matchedOffice.note}</div>}
              </div>
            )}
            {dashboardMode && hostCity && !matchedOffice && pa.city_offices?.length > 0 && (
              <div className="mt-5 border border-ink/15 rounded-xl p-4 bg-tint" data-testid="dash-fallback-office">
                <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Your host city · {hostCity}</div>
                <div className="mt-1 text-sm text-ink">
                  We don't have a direct link for {hostCity} yet. Use this formula:
                </div>
                <div className="mt-2 text-sm font-semibold text-ink">
                  {(pa.city_fallback_formula || "").replace("[city]", hostCity)}
                </div>
              </div>
            )}

            {/* National portal (e.g., France ANEF) */}
            {pa.national_portal?.url && (
              <div className="mt-5 border border-ink/15 rounded-lg p-4 hover:bg-tint transition-colors" data-testid="national-portal">
                <div className="flex items-start gap-3">
                  <Landmark size={18} className="text-elec mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-ink">{pa.national_portal.name}</div>
                    {pa.national_portal.note && <div className="mt-1 text-xs text-muted leading-snug">{pa.national_portal.note}</div>}
                    <a href={pa.national_portal.url} target="_blank" rel="noopener" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-elec hover:underline">
                      {pa.national_portal.url.replace(/^https?:\/\//, "")} <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* City-office grid (public dossier for city-based countries) */}
            {(!dashboardMode || !matchedOffice) && pa.city_offices?.length > 0 && (
              <div className="mt-5 grid sm:grid-cols-2 gap-2" data-testid="city-office-list">
                {pa.city_offices.map((c) => {
                  const isYours = hostCity && c.city.toLowerCase() === hostCity.toLowerCase();
                  return (
                    <a
                      key={c.city}
                      href={c.url}
                      target="_blank"
                      rel="noopener"
                      data-testid={`office-${c.city.toLowerCase()}`}
                      className={
                        "flex items-start gap-3 border rounded-lg px-4 py-3 hover:bg-tint transition-colors group " +
                        (isYours ? "border-elec bg-elec/5" : "border-ink/15")
                      }
                    >
                      <MapPin size={14} className="mt-0.5 text-elec shrink-0" />
                      <div className="min-w-0">
                        <div className="font-bold text-ink text-sm">{c.city}{isYours && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-elec text-white font-bold">YOUR CITY</span>}</div>
                        {c.note && <div className="text-[11px] text-muted leading-snug">{c.note}</div>}
                        <div className="text-[11px] text-elec truncate">{c.url.replace(/^https?:\/\//, "")}</div>
                      </div>
                      <ChevronRight size={14} className="ml-auto text-ink/40 group-hover:text-elec shrink-0" />
                    </a>
                  );
                })}
              </div>
            )}

            {pa.city_fallback_formula && !dashboardMode && (
              <div className="mt-4 text-[12px] text-muted">
                Other cities: <span className="font-semibold text-ink">{pa.city_fallback_formula}</span>
              </div>
            )}

            {/* Registration-category gotchas (Spain: TIE-not-needed · Poland: zameldowanie varies-by-city · Denmark: outdated DKK figures). */}
            {pa.categories?.find(c => c.key === "registration")?.gotchas?.length > 0 && (
              <div className="mt-5 space-y-3" data-testid="registration-gotchas">
                {pa.categories.find(c => c.key === "registration").gotchas.map((g, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-xl bg-[color:var(--moe-yolk)] text-ink"
                    data-testid={`registration-gotcha-${i}`}
                  >
                    <AlertTriangle size={18} className="text-ink shrink-0 mt-0.5" />
                    <div className="text-ink">
                      <div className="font-bold text-ink text-sm">{g.title}</div>
                      {g.detail && <div className="mt-1 text-[13px] text-ink leading-snug">{g.detail}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* HOME & ESSENTIALS */}
          {pa.categories?.find(c => c.key === "home")?.groups?.length > 0 && (
            <div className="mt-6 moe-card p-6 md:p-8" data-testid="section-home-essentials">
              <div className="text-xs uppercase tracking-widest text-elec font-bold">Home & essentials</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">SIM, groceries, marketplace.</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                {pa.categories.find(c => c.key === "home").groups.map((g) => (
                  <div key={g.label}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{g.label}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`home-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      {g.items.map(i => (
                        <span key={i} className="px-2.5 py-1 text-[12px] font-semibold border border-ink/25 rounded-full">{i}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {/* Free / non-monetised local alternatives (UK: Rightmove/Zoopla/SpareRoom). */}
              {pa.categories.find(c => c.key === "home").free_alternatives?.length > 0 && (
                <div className="mt-6" data-testid="home-free-alternatives">
                  {pa.categories.find(c => c.key === "home").free_alternatives.map((block) => (
                    <div key={block.label} className="mt-4 first:mt-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Free / non-monetised · {block.label}</div>
                      <div className="mt-2 grid sm:grid-cols-3 gap-2">
                        {block.items.map((i) => (
                          <a
                            key={i.name}
                            href={i.url}
                            target="_blank"
                            rel="noopener"
                            className="flex items-center justify-between gap-2 border border-dashed border-ink/25 rounded-lg px-3 py-2 hover:bg-tint transition-colors group"
                            data-testid={`free-alt-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            <div className="min-w-0">
                              <div className="font-bold text-ink text-sm truncate">{i.name}</div>
                              {i.note && <div className="text-[11px] text-muted truncate">{i.note}</div>}
                            </div>
                            <ExternalLink size={11} className="text-ink/50 group-hover:text-elec shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MONEY & DIGITAL */}
          {pa.categories?.find(c => c.key === "money")?.groups?.length > 0 && (
            <div className="mt-6 moe-card p-6 md:p-8" data-testid="section-money-digital">
              <div className="text-xs uppercase tracking-widest text-elec font-bold">Money & digital</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Bank, statutory health cover, doctor booking.</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                {pa.categories.find(c => c.key === "money").groups.map((g) => (
                  <div key={g.label}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{g.label}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`money-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      {g.items.map(i => (
                        <span key={i} className="px-2.5 py-1 text-[12px] font-semibold border border-ink/25 rounded-full">{i}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-[11px] text-muted">
                Blocked account, forex and insurance partners → see <span className="font-semibold text-ink">Nikshala partner services</span> above.
              </div>
            </div>
          )}

          {/* MOBILITY */}
          {pa.categories?.find(c => c.key === "mobility")?.groups?.length > 0 && (
            <div className="mt-6 moe-card p-6 md:p-8" data-testid="section-mobility">
              <div className="text-xs uppercase tracking-widest text-elec font-bold">Mobility</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Trains, rideshare, rentals, cross-Europe rail.</h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {pa.categories.find(c => c.key === "mobility").groups.map((g) => (
                  <div key={g.label}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{g.label}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {g.items.map((i) => {
                        // Founder rule (2026-02-20): "Eurail" and "Interrail" pills must
                        // link to eurail.com — the correct booking site for non-EU residents
                        // (including Indian citizens). Interrail is EU-residents only, so we
                        // deliberately route both labels to Eurail.
                        const lc = (i || "").toLowerCase();
                        const isRailPass = lc === "eurail" || lc === "interrail" || lc.includes("eurail") || lc.includes("interrail");
                        if (isRailPass) {
                          return (
                            <a
                              key={i}
                              href="https://www.eurail.com/en"
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`mobility-rail-link-${lc.replace(/\s+/g,'-')}`}
                              className="px-2.5 py-1 text-[12px] font-semibold border border-elec text-elec rounded-full hover:bg-elec hover:text-white transition-colors inline-flex items-center gap-1"
                              title="Book Eurail passes (for non-EU residents including Indian citizens)"
                            >
                              {i}
                              <ArrowUpRight size={11} strokeWidth={2.4} />
                            </a>
                          );
                        }
                        return (
                          <span key={i} className="px-2.5 py-1 text-[12px] font-semibold border border-ink/25 rounded-full">{i}</span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {/* Category-level gotchas (UK: Eurail/Interrail doesn't cover UK / Eurostar). */}
              {pa.categories.find(c => c.key === "mobility").gotchas?.length > 0 && (
                <div className="mt-5 space-y-3" data-testid="mobility-gotchas">
                  {pa.categories.find(c => c.key === "mobility").gotchas.map((g, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-4 rounded-xl border-2 border-[color:var(--moe-yolk)] bg-[color:var(--moe-yolk)]/20"
                      data-testid={`gotcha-${i}`}
                    >
                      <AlertTriangle size={18} className="text-ink shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold text-ink text-sm">{g.title}</div>
                        {g.detail && <div className="mt-1 text-[13px] text-ink leading-snug">{g.detail}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
          )}

          {/* Dashboard-only merged Local Life tabs. Reference material,
              no repetition of task-board content. */}
          {compactMerged && (
            <LocalLifeTabs
              pa={pa}
              hostCity={hostCity}
              matchedOffice={matchedOffice}
            />
          )}
        </>
      )}

      {/* HONEST FALLBACK. Playbook in progress state for countries without a
          full post_arrival document yet. Renders only after the fetch resolves
          to 404 so we don't flash it during the initial load. */}
      {paLoaded && !pa && (
        <div className="mt-10 moe-card p-6 md:p-8" data-testid="section-playbook-in-progress">
          <div className="flex items-center gap-2">
            <div className="text-xs uppercase tracking-widest text-elec font-bold">Playbook in progress</div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-elec/10 text-elec">
              Coming soon
            </span>
          </div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
            {dashboardMode
              ? `Full post-arrival playbook for ${country} is next in line`
              : `${country}: full post-arrival playbook coming soon`}
          </h2>
          <p className="mt-3 text-sm text-muted max-w-2xl">
            The visa data above is confirmed against the country's official source. The registration steps,
            SIM / bank / supermarket rails, mobility partners, city-specific offices and Nikshala partner
            services haven't been fully verified for this country yet. Rather than surface partial or
            guessed content, we hold it back until it's checked. The Concierge tier covers this in the
            meantime, if you need it now.
          </p>
        </div>
      )}
    </>
  );
}
